import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, erroMsg } from "../../lib/api";
import { brl, parsePctInput, pct, toPctInput } from "../../lib/format";
import { useMeta } from "../../context/MetaContext";
import { ProductImage } from "../../components/ProductImage";
import { Loader2, Pencil, Plus, Search, Trash2, Upload, X } from "lucide-react";

const vazio = {
  origem: "FABRICAÇÃO",
  codigo: "",
  descricao_tecnica: "",
  descricao_produto: "",
  unidade: "PÇ",
  ncm: "",
  valor_unitario: 0,
  ipi: 0,
  desconto_maximo: 0.4,
  nota_icms: "",
  nota_st: "",
  observacao: "",
  ativo: true,
  foto_path: null,
};

export default function AdminProdutos() {
  const { ncms, origens, recarregar } = useMeta();
  const [params] = useSearchParams();

  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [debounced, setDebounced] = useState("");
  const [ncmF, setNcmF] = useState("todos");
  const [origemF, setOrigemF] = useState("todas");
  const [precoF, setPrecoF] = useState(params.get("filtro") === "sem_preco" ? "sem_preco" : "todos");
  const [ativoF, setAtivoF] = useState("todos");
  const [pagina, setPagina] = useState(0);
  const porPagina = 25;

  const [aberto, setAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(vazio);
  const [ipiTexto, setIpiTexto] = useState("0");
  const [descTexto, setDescTexto] = useState("40");
  const [salvando, setSalvando] = useState(false);
  const [excluir, setExcluir] = useState(null);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(busca);
      setPagina(0);
    }, 300);
    return () => clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get("/admin/products", {
        params: {
          q: debounced,
          ncm: ncmF === "todos" ? "" : ncmF,
          origem: origemF === "todas" ? "" : origemF,
          status_preco: precoF,
          status_ativo: ativoF,
          limit: porPagina,
          skip: pagina * porPagina,
        },
      });
      setItens(r.data.itens);
      setTotal(r.data.total);
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setCarregando(false);
    }
  }, [debounced, ncmF, origemF, precoF, ativoF, pagina]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirNovo = () => {
    setEditando(null);
    setForm({ ...vazio, ncm: ncms[0]?.codigo || "" });
    setIpiTexto("0");
    setDescTexto("40");
    setAberto(true);
  };

  const abrirEdicao = (p) => {
    setEditando(p);
    setForm({ ...vazio, ...p });
    setIpiTexto(toPctInput(p.ipi, 4));
    setDescTexto(toPctInput(p.desconto_maximo, 2));
    setAberto(true);
  };

  const salvar = async () => {
    if (!form.codigo.trim() || !form.descricao_produto.trim() || !form.ncm) {
      toast.error("Preencha código, descrição do produto e NCM.");
      return;
    }
    setSalvando(true);
    const payload = {
      ...form,
      valor_unitario: Number(form.valor_unitario) || 0,
      ipi: parsePctInput(ipiTexto),
      desconto_maximo: parsePctInput(descTexto),
    };
    try {
      if (editando) {
        await api.put(`/admin/products/${editando.id}`, payload);
        toast.success("Produto atualizado");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produto criado");
      }
      setAberto(false);
      carregar();
      recarregar();
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setSalvando(false);
    }
  };

  const confirmarExclusao = async () => {
    try {
      await api.delete(`/admin/products/${excluir.id}`);
      toast.success("Produto excluído");
      setExcluir(null);
      carregar();
    } catch (e) {
      toast.error(erroMsg(e));
    }
  };

  const enviarFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editando) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande (máximo 5MB).");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setEnviandoFoto(true);
    try {
      const r = await api.post(`/admin/products/${editando.id}/foto`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, foto_path: r.data.foto_path }));
      setEditando((ed) => (ed ? { ...ed, foto_path: r.data.foto_path } : ed));
      carregar();
      toast.success("Foto enviada com sucesso");
    } catch (err) {
      toast.error(erroMsg(err));
    } finally {
      setEnviandoFoto(false);
      e.target.value = "";
    }
  };

  const removerFoto = async () => {
    if (!editando) return;
    try {
      await api.delete(`/admin/products/${editando.id}/foto`);
      setForm((f) => ({ ...f, foto_path: null }));
      setEditando((ed) => (ed ? { ...ed, foto_path: null } : ed));
      carregar();
      toast.success("Foto removida");
    } catch (err) {
      toast.error(erroMsg(err));
    }
  };

  const paginas = Math.ceil(total / porPagina) || 1;

  return (
    <AdminShell
      titulo="Produtos"
      descricao={`${total} produto(s) no filtro atual. Produtos com preço R$ 0,00 não aparecem para os representantes.`}
      acoes={
        <Button onClick={abrirNovo} data-testid="admin-produtos-novo-button">
          <Plus className="mr-1.5 h-4 w-4" /> Novo produto
        </Button>
      }
    >
      <div className="mb-3 flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por código, descrição ou NCM…"
            className="h-10 pl-9"
            data-testid="admin-produtos-busca-input"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={ncmF} onValueChange={(v) => { setNcmF(v); setPagina(0); }}>
            <SelectTrigger className="h-10 w-[140px]" data-testid="admin-produtos-filtro-ncm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">Todos NCMs</SelectItem>
              {ncms.map((n) => (
                <SelectItem key={n.codigo} value={n.codigo}>
                  {n.codigo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={origemF} onValueChange={(v) => { setOrigemF(v); setPagina(0); }}>
            <SelectTrigger className="h-10 w-[135px]" data-testid="admin-produtos-filtro-origem">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todas">Toda origem</SelectItem>
              {origens.map((o) => (
                <SelectItem key={o} value={o}>
                  {o === "IMPORTAÇÃO" ? "Importação" : "Fabricação"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={precoF} onValueChange={(v) => { setPrecoF(v); setPagina(0); }}>
            <SelectTrigger className="h-10 w-[150px]" data-testid="admin-produtos-filtro-preco">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">Com e sem preço</SelectItem>
              <SelectItem value="com_preco">Somente com preço</SelectItem>
              <SelectItem value="sem_preco">Somente sem preço</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ativoF} onValueChange={(v) => { setAtivoF(v); setPagina(0); }}>
            <SelectTrigger className="h-10 w-[130px]" data-testid="admin-produtos-filtro-ativo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">Ativos e inativos</SelectItem>
              <SelectItem value="ativo">Somente ativos</SelectItem>
              <SelectItem value="inativo">Somente inativos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table data-testid="admin-produtos-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Código</TableHead>
                <TableHead className="min-w-[260px]">Produto</TableHead>
                <TableHead className="w-[110px]">NCM</TableHead>
                <TableHead className="w-[70px]">Un.</TableHead>
                <TableHead className="w-[110px] text-right">Valor</TableHead>
                <TableHead className="w-[80px] text-right">IPI</TableHead>
                <TableHead className="w-[90px] text-right">Desc. máx.</TableHead>
                <TableHead className="w-[130px]">Status</TableHead>
                <TableHead className="w-[100px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {carregando
                ? Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={9}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : itens.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-produto-row-${p.codigo}`}>
                      <TableCell className="font-mono-num text-[12px]">{p.codigo}</TableCell>
                      <TableCell>
                        <div className="text-[13px] font-medium text-slate-900">
                          {p.descricao_produto}
                        </div>
                        <div className="line-clamp-1 text-[11px] text-slate-500">
                          {p.descricao_tecnica}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono-num text-[12px]">{p.ncm}</TableCell>
                      <TableCell className="text-[12px]">{p.unidade}</TableCell>
                      <TableCell className="num text-right text-[13px] font-medium">
                        {brl(p.valor_unitario)}
                      </TableCell>
                      <TableCell className="num text-right text-[12px]">{pct(p.ipi)}</TableCell>
                      <TableCell className="num text-right text-[12px]">
                        {pct(p.desconto_maximo)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {p.sem_preco && (
                            <Badge className="bg-amber-100 text-[10px] text-amber-900">
                              Sem preço
                            </Badge>
                          )}
                          {!p.ativo && (
                            <Badge variant="secondary" className="text-[10px]">
                              Inativo
                            </Badge>
                          )}
                          {p.ativo && !p.sem_preco && (
                            <Badge className="bg-[#EEF2F9] text-[10px] text-[#1B315E]">Publicado</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => abrirEdicao(p)}
                            data-testid={`admin-produto-editar-${p.codigo}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-destructive"
                            onClick={() => setExcluir(p)}
                            data-testid={`admin-produto-excluir-${p.codigo}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              {!carregando && itens.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-sm text-slate-500">
                    Nenhum produto encontrado com esses filtros.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2.5">
          <span className="text-[12px] text-slate-500">
            Página {pagina + 1} de {paginas} • {total} registros
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina === 0}
              onClick={() => setPagina((p) => p - 1)}
              data-testid="admin-produtos-anterior"
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina + 1 >= paginas}
              onClick={() => setPagina((p) => p + 1)}
              data-testid="admin-produtos-proxima"
            >
              Próxima
            </Button>
          </div>
        </div>
      </div>

      <Sheet open={aberto} onOpenChange={setAberto}>
        <SheetContent
          className="w-full overflow-y-auto bg-white sm:max-w-xl"
          data-testid="admin-produtos-drawer"
        >
          <SheetHeader>
            <SheetTitle>{editando ? "Editar produto" : "Novo produto"}</SheetTitle>
            <SheetDescription>
              As alíquotas de ST/DIFAL vêm da matriz do NCM selecionado.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-4 pb-8">
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
              <ProductImage
                fotoPath={form.foto_path}
                alt={form.descricao_produto}
                className="h-16 w-16 rounded-lg"
                iconClassName="h-6 w-6"
              />
              <div className="flex-1">
                <div className="text-[13px] font-medium text-slate-900">Foto do produto</div>
                {editando ? (
                  <div className="mt-1.5 flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={enviandoFoto}
                      data-testid="admin-produto-foto-upload-button"
                    >
                      {enviandoFoto ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Upload className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {form.foto_path ? "Trocar foto" : "Enviar foto"}
                    </Button>
                    {form.foto_path && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 text-slate-400 hover:text-destructive"
                        onClick={removerFoto}
                        data-testid="admin-produto-foto-remover-button"
                      >
                        <X className="mr-1 h-3.5 w-3.5" /> Remover
                      </Button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={enviarFoto}
                      data-testid="admin-produto-foto-input"
                    />
                  </div>
                ) : (
                  <p className="mt-1 text-[11.5px] text-slate-500">
                    Salve o produto para poder adicionar uma foto.
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Código *</Label>
                <Input
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                  className="h-10 font-mono-num"
                  data-testid="admin-produto-codigo"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Origem</Label>
                <Select value={form.origem} onValueChange={(v) => setForm({ ...form, origem: v })}>
                  <SelectTrigger className="h-10" data-testid="admin-produto-origem">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="FABRICAÇÃO">Fabricação</SelectItem>
                    <SelectItem value="IMPORTAÇÃO">Importação</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Descrição do produto *</Label>
              <Input
                value={form.descricao_produto}
                onChange={(e) => setForm({ ...form, descricao_produto: e.target.value })}
                className="h-10"
                data-testid="admin-produto-descricao"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Descrição técnica</Label>
              <Textarea
                value={form.descricao_tecnica}
                onChange={(e) => setForm({ ...form, descricao_tecnica: e.target.value })}
                rows={3}
                data-testid="admin-produto-descricao-tecnica"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>NCM *</Label>
                <Select value={form.ncm} onValueChange={(v) => setForm({ ...form, ncm: v })}>
                  <SelectTrigger className="h-10" data-testid="admin-produto-ncm">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {ncms.map((n) => (
                      <SelectItem key={n.codigo} value={n.codigo}>
                        {n.codigo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Unidade</Label>
                <Input
                  value={form.unidade}
                  onChange={(e) => setForm({ ...form, unidade: e.target.value })}
                  className="h-10"
                  data-testid="admin-produto-unidade"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Valor unitário (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.valor_unitario}
                  onChange={(e) => setForm({ ...form, valor_unitario: e.target.value })}
                  className="h-10 num"
                  data-testid="admin-produto-valor"
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>IPI (%)</Label>
                <Input
                  inputMode="decimal"
                  value={ipiTexto}
                  onChange={(e) => setIpiTexto(e.target.value)}
                  className="h-10 num"
                  placeholder="3,25"
                  data-testid="admin-produto-ipi"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Desconto máximo (%)</Label>
                <Input
                  inputMode="decimal"
                  value={descTexto}
                  onChange={(e) => setDescTexto(e.target.value)}
                  className="h-10 num"
                  placeholder="40"
                  data-testid="admin-produto-desconto-maximo"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Observação (aparece para o representante)</Label>
              <Input
                value={form.observacao}
                onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                className="h-10"
                placeholder="Ex.: SOB ENCOMENDA LOTE MINIMO 5KM"
                data-testid="admin-produto-observacao"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Nota de ST</Label>
              <Textarea
                value={form.nota_st}
                onChange={(e) => setForm({ ...form, nota_st: e.target.value })}
                rows={2}
                data-testid="admin-produto-nota-st"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <div>
                <div className="text-[13px] font-medium text-slate-900">Produto ativo</div>
                <div className="text-[11.5px] text-slate-500">
                  Inativos não aparecem na consulta dos representantes.
                </div>
              </div>
              <Switch
                checked={form.ativo}
                onCheckedChange={(v) => setForm({ ...form, ativo: v })}
                data-testid="admin-produto-ativo-switch"
              />
            </div>

            {Number(form.valor_unitario) <= 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                Com valor R$ 0,00 este produto fica oculto para os representantes.
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                className="h-11 flex-1"
                onClick={salvar}
                disabled={salvando}
                data-testid="admin-produto-salvar"
              >
                {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editando ? "Salvar alterações" : "Criar produto"}
              </Button>
              <Button variant="outline" className="h-11" onClick={() => setAberto(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!excluir} onOpenChange={(o) => !o && setExcluir(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir produto?</AlertDialogTitle>
            <AlertDialogDescription>
              {excluir?.codigo} — {excluir?.descricao_produto}. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmarExclusao}
              data-testid="admin-produto-excluir-confirmar"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
